<template>
	<div>
		<div v-if="!noHeader" class="header-container px-5">
			<IntegrationTitle
				v-if="selectedApp"
				:noBackButton="noBackButton || isEndRun"
				:searchString.sync="searchString"
				:loading="loading"
				:selectedApp="selectedApp"
				:title="title"
				:isTrigger="isTrigger"
				@showSelectAppScreen="showSelectAppScreen"
			/>
		</div>
		<div v-if="filteredActions.length > 0">
			<div v-for="(items, categoryName) in itemsByCategories" :key="categoryName">
				<template v-if="items?.length">
					<div class="title-container display-flex flex-row" :class="{ 'mt-3': !noHeader }">
						<div class="title-text">{{ categoryName }}</div>
					</div>
					<div v-for="action in items" :key="action.id" class="actions-container">
						<ActionMenuItem
							:action="action"
							:focusedItemUUID="focusedItemUUID"
							:showAppNamePreview="showAppNamePreview"
							@action-selected="onActionSelected"
							@scroll-to-element="onScrollToElement"
						/>
					</div>
				</template>
			</div>
			<div
				v-if="selectedApp && !isTrigger"
				@click="openAskForIntegrationDialog"
				class="ask-for-intigration-container display-flex flex-row flex-centerize"
			>
				<SIcon name="ask-for-integration" class="ask-for-integration" />
				{{ cannyTitle }}
			</div>
		</div>
		<AppLoader v-else-if="loading || isLoadingAppActions" class="width-100 flex-centerize mt-6" />
		<div v-else-if="!noHeader" class="no-results width-100 flex-centerize mt-6">No results</div>
	</div>
</template>

<script>
import { ServiceSlugs } from '@ply-io/ply-front-shared-utilities';

import ActionMenuItem from './ActionMenuItem';
import IntegrationTitle from '../IntegrationEditor/IntegrationTitle';
import AppDialogManagerService from '@/components/dialogs/AppDialogManager/app-dialog-manager-service';
import { getServiceBySlug } from '@/store/modules/apps/getters';
import { isActionAllowedForSelectedWorkflow } from '@/store/modules/selectedWorkflow/getters';
import { isEndRunNodePossible } from '@/store/modules/treeBlocks/getters';
import { mapGettersShorthand } from '@/store/utils/mappers';

const CONTEXTUAL_ACTIONS = 'contextual actions';

export default {
	name: 'ActionsMenu',
	props: {
		items: { type: Array, required: true },
		title: { type: String, default: 'Choose action' },
		loading: { type: Boolean },
		noHeader: { type: Boolean },
		noBackButton: { type: Boolean },
		isTrigger: { type: Boolean },
		focusedItemUUID: { type: String },
		showAppNamePreview: { type: Boolean },
		selectedApp: { type: Object },
		nodeId: { type: [Number, String] },
	},
	components: { ActionMenuItem, IntegrationTitle },
	computed: {
		...mapGettersShorthand({ isEndRunNodePossible, isActionAllowedForSelectedWorkflow, getServiceBySlug }),
		subcategoryTags() {
			return Array.isArray(this.selectedApp?.tagged_items) ? [...this.selectedApp.tagged_items].sort((a, b) => a.order - b.order) : [];
		},
		cannyTitle() {
			return `Ask for an action / Give feedback about ${this.selectedApp.service_name}`;
		},
		appTotalActions() {
			return this.items;
		},
		isLoadingAppActions() {
			return this.selectedApp && this.items.length === 0;
		},
		filteredActions() {
			const sc = this.searchString || '';
			const allActions = this.appTotalActions;
			const filtered = allActions
				.filter(({ title, description }) =>
					sc
						.toLowerCase()
						.split(' ')
						.every((word) => title.toLowerCase().includes(word) || description.toLowerCase().includes(word))
				)
				.filter(this.isActionAllowedForSelectedWorkflow)
				.filter((action) => action.id !== this.endRunService.actionId || this.isEndRunNodePossible(this.nodeId));
			return filtered;
		},
		itemsByCategories() {
			const categories = {};
			const allSubcategories = this.subcategoryTags.concat({ name: CONTEXTUAL_ACTIONS }); // adds contextual actions as a subcategory
			allSubcategories.forEach((taggedCategory) => {
				if (taggedCategory.name && taggedCategory.tag_id) {
					categories[taggedCategory.name] = this.filteredActions.filter(
						(action) =>
							action.subcategory?.tag_id === taggedCategory.tag_id || (taggedCategory.name === CONTEXTUAL_ACTIONS && !!action.browserAction)
					);
				}
			});
			const taggedActions = [].concat(...Object.values(categories)); // creats a single-dimension array, of the tagged actions
			const tagLessActions = this.filteredActions.filter(
				(action) => taggedActions.findIndex((taggedAction) => taggedAction?.id === action.id) === -1
			);

			const moreActionsTitle = Object.values(categories).filter((actions) => actions?.length > 0)?.length > 0 ? 'More actions' : this.title;
			return { ...categories, [moreActionsTitle]: tagLessActions };
		},
		endRunService() {
			return this.getServiceBySlug(ServiceSlugs.EndRun);
		},
		isEndRun() {
			return this.selectedApp?.service_slug === ServiceSlugs.EndRun;
		},
	},
	methods: {
		async openAskForIntegrationDialog() {
			await AppDialogManagerService.openIntegrationDialog();
		},
		showSelectAppScreen() {
			this.$emit('showSelectAppScreen');
		},
		onActionSelected(action) {
			this.$emit('action-selected', action);
		},
		onScrollToElement(yPosition) {
			this.$emit('scroll-to-element', yPosition);
		},
	},
	data: () => ({ searchString: '' }),
};
</script>

<style scoped lang="scss">
$side-padding: 20px;
.title-container {
	width: 100%;
	text-transform: capitalize;
	padding: 8px $side-padding;
	background-color: var(--sub-menu-background);
	.title-text {
		font-size: 12px;
		margin-top: -6px;
		color: var(--bold-title);
	}
	.see-all-link {
		cursor: pointer;
		font-size: 12px;
		color: var(--primary-color);
	}
}
.actions-container {
	.action-container {
		width: 100%;
		cursor: pointer;
		padding: 6px $side-padding;
		&:hover {
			background-color: var(--app-selection-hover-background);
		}
	}
}
.no-results {
	font-size: 14px;
}
.ask-for-integration {
	margin: 8px;
}

.ask-for-intigration-container {
	width: 100%;
	height: 50px;
	cursor: pointer;
	font-size: 14px;
	margin: 8px 0 -30px 0;
	color: var(--primary-color);
	background-color: var(--ask-for-ingreation-background);

	&:hover {
		background-color: var(--ask-for-ingreation-hover-background);
	}
}
</style>
